import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import './TimeZonePicker.css';

// Lowercased, with the separators in zone names ("America/St_Johns") and in how people
// write places ("St. John's") reduced to spaces, so "john" and "st johns" both match. An
// offset keeps its sign and loses its leading zero, so "-3", "utc-03" and "-03:00" find
// UTC-03:00 and not UTC+03:00. Brackets go, so the shown "Halifax (UTC-03:00)" searches too.
const searchable = (text) =>
  text
    .toLowerCase()
    .replace(/['’()]/g, '')
    .replace(/([+-])0(?=\d)/g, '$1')
    .replace(/-(?!\d)/g, ' ')
    .replace(/[_/.,]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// A typed offset hour is a whole number, so "+1" and "+01" find UTC+01:00 and not UTC+10:00
// to UTC+14:00. Anything else matches anywhere in the name or offset.
const matcherFor = (q) => {
  if (!/[+-]\d{1,2}$/.test(q)) return (text) => text.includes(q);
  const pattern = new RegExp(`${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?!\\d)`);
  return (text) => pattern.test(text);
};

/**
 * A searchable list of time zones (WAI-ARIA combobox with a listbox popup).
 *
 * `zones` is the server's list, `[{ name, utc_offset, featured }]`, featured zones first.
 * It is the list the save validates against, so anything picked here saves. A stored value
 * the list does not offer (an older alias) is shown as it is, never replaced silently.
 *
 * The list opens on click, typing or ArrowDown, not on focus, so tabbing through the form
 * passes over it quietly. Opened, it keeps showing the current zone, selected, until the
 * person types: what is set stays visible, and typing replaces it as a search.
 *
 * `status` is the list's own: while it is `loading` or after an `error` the picker says so,
 * rather than reading as a search with no matches, and an error offers `onRetry`.
 *
 * `noZoneLabel`, when given, is a first option that stands for no zone of its own (such as
 * "Use my device time zone"): choosing it sets the value to '', and an empty value shows it.
 */
export default function TimeZonePicker({
  id,
  value,
  onChange,
  zones = [],
  disabled = false,
  required = false,
  invalid = false,
  placeholder = 'Search time zones',
  describedBy,
  status = 'ready',
  onRetry,
  noZoneLabel,
}) {
  const listId = useId();
  const optionId = (i) => `${listId}-opt-${i}`;
  const groupId = (n) => `${listId}-group-${n}`;
  const statusId = `${listId}-status`;
  const [open, setOpen] = useState(false);
  // null until the person types: the box keeps showing the current zone.
  const [query, setQuery] = useState(null);
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const offsetOf = useMemo(() => Object.fromEntries(zones.map((z) => [z.name, z.utc_offset])), [zones]);
  const display = value ? (offsetOf[value] ? `${value} (${offsetOf[value]})` : value) : (noZoneLabel || '');

  // The no-zone option, when there is one, leads the list and is searchable by its label.
  const matches = useMemo(() => {
    const q = searchable(query || '');
    const noZone = noZoneLabel ? [{ name: '', label: noZoneLabel }] : [];
    if (!q) return [...noZone, ...zones];
    const matching = matcherFor(q);
    return [
      ...noZone.filter((z) => searchable(z.label).includes(q)),
      ...zones.filter((z) => matching(searchable(`${z.name} ${z.utc_offset}`))),
    ];
  }, [zones, query, noZoneLabel]);
  const leading = matches.length > 0 && matches[0].name === '' ? 1 : 0;
  const featuredCount = matches.filter((z) => z.featured).length;

  const openList = () => {
    if (disabled) return;
    setQuery(null);
    const all = noZoneLabel ? [{ name: '' }, ...zones] : zones;
    const current = all.findIndex((z) => z.name === (value || ''));
    setActive(current >= 0 ? current : 0);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setQuery(null);
  };
  const choose = (zone) => {
    if (zone) onChange(zone.name);
    close();
  };
  const moveTo = (i) => {
    const next = Math.max(0, Math.min(i, matches.length - 1));
    setActive(next);
    const el = listRef.current?.querySelector(`#${CSS.escape(optionId(next))}`);
    el?.scrollIntoView?.({ block: 'nearest' });
  };

  // Select the shown zone so typing replaces it rather than editing its text. Again after the
  // click has finished: a click landing on text that is already selected (as it is after
  // tabbing in) has the browser place the caret after the click handler runs.
  const selectAllAfterClick = (el) => {
    el.select();
    setTimeout(() => { if (document.activeElement === el) el.select(); }, 0);
  };

  // On opening, bring the current zone into view.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [open]);

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) {
      e.preventDefault();
      openList();
      e.currentTarget.select();
      return;
    }
    // Typing with the list closed (after a pick, or Escape) starts a new search: the shown
    // zone is selected first so the key replaces it instead of adding to its text.
    if (!open && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      openList();
      e.currentTarget.select();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); moveTo(active + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveTo(active - 1); }
    else if (e.key === 'Home') { e.preventDefault(); moveTo(0); }
    else if (e.key === 'End') { e.preventDefault(); moveTo(matches.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(matches[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
  };

  const renderOption = (zone, i) => (
    <li
      key={zone.name || 'no-zone'}
      id={optionId(i)}
      role="option"
      aria-selected={zone.name === (value || '')}
      className={`tz-picker__option${i === active ? ' tz-picker__option--active' : ''}`}
      // mousedown, not click: the input's blur would otherwise close the list first.
      onMouseDown={(e) => { e.preventDefault(); choose(zone); }}
      onMouseEnter={() => setActive(i)}
    >
      <span className="tz-picker__name">{zone.label || zone.name}</span>
      {zone.utc_offset && <span className="tz-picker__offset">{zone.utc_offset}</span>}
    </li>
  );

  const group = (n, label, options, start) => (
    <li role="group" aria-labelledby={groupId(n)} className="tz-picker__section">
      <div id={groupId(n)} role="presentation" className="tz-picker__group">{label}</div>
      <ul role="none" className="tz-picker__options">
        {options.map((z, i) => renderOption(z, start + i))}
      </ul>
    </li>
  );
  // Only to someone who can pick: a read-only view still shows the stored zone.
  const showError = status === 'error' && !disabled;
  const featured = matches.slice(leading, leading + featuredCount);
  const others = matches.slice(leading + featuredCount);
  const emptyText =
    status === 'loading' ? 'Loading time zones…'
      : status === 'error' ? 'Couldn’t load time zones.'
        : `No time zone matches “${query || ''}”`;

  return (
    <div className="tz-picker">
      <input
        id={id}
        ref={inputRef}
        type="text"
        role="combobox"
        className={`resui-input settings-org-input tz-picker__input${invalid ? ' resui-input--error' : ''}`}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={[describedBy, showError ? statusId : null].filter(Boolean).join(' ') || undefined}
        aria-activedescendant={open && matches.length ? optionId(active) : undefined}
        value={open && query !== null ? query : display}
        placeholder={open ? placeholder : (value ? '' : 'Choose a time zone')}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        onClick={(e) => { if (!open) { openList(); selectAllAfterClick(e.currentTarget); } }}
        onBlur={close}
        onChange={(e) => { setQuery(e.target.value); setActive(0); if (!open) setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {open && (
        // mousedown anywhere in the list (a heading, the padding, the scrollbar) keeps focus
        // in the input, so the list stays open; an option's own handler then picks it.
        <ul
          id={listId}
          ref={listRef}
          role="listbox"
          className="tz-picker__list"
          aria-label="Time zones"
          onMouseDown={(e) => e.preventDefault()}
        >
          {matches.length === leading && (leading === 0 || status !== 'ready') && (
            <li className="tz-picker__empty" role="presentation">{emptyText}</li>
          )}
          {leading > 0 && renderOption(matches[0], 0)}
          {featured.length > 0 && group('featured', 'Canada', featured, leading)}
          {others.length > 0 && group('others', featured.length > 0 ? 'All other time zones' : 'Time zones', others, leading + featuredCount)}
        </ul>
      )}
      {showError && (
        <p id={statusId} className="tz-picker__status" role="alert">
          Couldn’t load time zones.{' '}
          {onRetry && (
            <button type="button" className="tz-picker__retry" onClick={onRetry}>Try again</button>
          )}
        </p>
      )}
    </div>
  );
}
